import { create } from 'zustand';
import {
  researchRunsApi,
  type ResearchRunDetail,
  type ResearchRunSummary,
} from '@/services/api';

interface RunsFilters {
  clientUid: string;
  researchType: string;
  status: string;
}

interface RunsState {
  runs: ResearchRunSummary[];
  selectedRunSummary: ResearchRunSummary | null;
  selectedRunDetail: ResearchRunDetail | null;
  filters: RunsFilters;

  loading: boolean;
  detailLoading: boolean;
  error: string | null;

  pollHandle: ReturnType<typeof setInterval> | null;

  setFilters: (filters: Partial<RunsFilters>) => void;
  selectRun: (summary: ResearchRunSummary | null) => void;
  fetchRuns: () => Promise<void>;
  fetchRunDetail: (id: string) => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

const POLL_INTERVAL_MS = 10_000;

function hasRunning(runs: ResearchRunSummary[]): boolean {
  return runs.some((r) => r.status === 'running' || r.status === 'in_progress');
}

export const useRunsStore = create<RunsState>((set, get) => ({
  runs: [],
  selectedRunSummary: null,
  selectedRunDetail: null,
  filters: { clientUid: '', researchType: '', status: '' },

  loading: false,
  detailLoading: false,
  error: null,

  pollHandle: null,

  setFilters: (partial) => {
    set((s) => ({ filters: { ...s.filters, ...partial } }));
    get().fetchRuns();
  },

  selectRun: (summary) => {
    set({
      selectedRunSummary: summary,
      selectedRunDetail: null,
    });
    if (summary) {
      get().fetchRunDetail(summary.id);
    }
  },

  fetchRuns: async () => {
    const { filters } = get();
    set({ loading: true, error: null });
    try {
      const res = await researchRunsApi.list({
        client_uid: filters.clientUid || undefined,
        research_type: filters.researchType || undefined,
        status: filters.status || undefined,
        limit: 100,
      });
      set({ runs: res.runs, loading: false });

      // Auto-manage polling based on whether any run is currently running.
      if (hasRunning(res.runs)) {
        get().startPolling();
      } else {
        get().stopPolling();
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchRunDetail: async (id) => {
    set({ detailLoading: true });
    try {
      const detail = await researchRunsApi.detail(id);
      set({ selectedRunDetail: detail, detailLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, detailLoading: false });
    }
  },

  startPolling: () => {
    const { pollHandle } = get();
    if (pollHandle) return; // Already polling
    const handle = setInterval(() => {
      // Re-fetch quietly; loading flag stays false to avoid UI flicker.
      researchRunsApi
        .list({
          client_uid: get().filters.clientUid || undefined,
          research_type: get().filters.researchType || undefined,
          status: get().filters.status || undefined,
          limit: 100,
        })
        .then((res) => {
          set({ runs: res.runs });
          if (!hasRunning(res.runs)) {
            get().stopPolling();
          }
          const sel = get().selectedRunSummary;
          if (sel) {
            researchRunsApi
              .detail(sel.id)
              .then((d) => set({ selectedRunDetail: d }))
              .catch(() => {
                /* silent — will retry on next tick */
              });
          }
        })
        .catch(() => {
          /* silent — will retry on next tick */
        });
    }, POLL_INTERVAL_MS);
    set({ pollHandle: handle });
  },

  stopPolling: () => {
    const { pollHandle } = get();
    if (pollHandle) {
      clearInterval(pollHandle);
      set({ pollHandle: null });
    }
  },
}));
